import React, { useState, useEffect } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';

const ReviewsCarousel = ({ 
  googleReviews, 
  loadingReviews,
  reviewsPerPage = 4,
  autoScrollInterval = 5000 
}) => {
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);

  // Auto-scroll reviews carousel
  useEffect(() => {
    if (!googleReviews?.reviews?.length) return;
    
    const totalPages = Math.ceil(googleReviews.reviews.length / reviewsPerPage);
    const interval = setInterval(() => {
      setCurrentReviewIndex(prev => (prev + 1) % totalPages);
    }, autoScrollInterval);
    
    return () => clearInterval(interval);
  }, [googleReviews, reviewsPerPage, autoScrollInterval]);

  if (loadingReviews) {
    return (
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto">
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-chocolate"></div>
          </div>
        </div>
      </section>
    );
  }

  const totalPages = googleReviews?.reviews ? Math.ceil(googleReviews.reviews.length / reviewsPerPage) : 0;

  return (
    <section className="py-20 px-6 bg-white">
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" alt="Google" className="h-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-chocolate">Lo que dicen nuestros clientes</h2>
          </div>
          {googleReviews && (
            <div className="flex items-center justify-center gap-2">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-6 h-6 ${
                      i < Math.floor(googleReviews.average_rating)
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-300'
                    }`}
                  />
                ))}
              </div>
              <span className="text-2xl font-bold text-chocolate">{googleReviews.average_rating}</span>
              <span className="text-gray-600">• {googleReviews.total_reviews} reseñas</span>
            </div>
          )}
        </div>

        {googleReviews?.reviews?.length > 0 ? (
          <div className="relative">
            {/* Reviews Carousel with Auto-scroll */}
            <div className="overflow-hidden">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentReviewIndex * 100}%)` }}
              >
                {/* Group reviews into pages */}
                {Array.from({ length: totalPages }).map((_, pageIndex) => (
                  <div key={pageIndex} className="w-full flex-shrink-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 px-4">
                      {googleReviews.reviews
                        .slice(pageIndex * reviewsPerPage, (pageIndex + 1) * reviewsPerPage)
                        .map((review, index) => (
                          <div
                            key={`${pageIndex}-${index}`}
                            className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-5 border border-gray-100"
                          >
                            {/* Google Logo & Rating */}
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className="flex">
                                  {[...Array(5)].map((_, i) => (
                                    <Star
                                      key={i}
                                      className={`w-4 h-4 ${
                                        i < review.rating
                                          ? 'text-yellow-400 fill-yellow-400'
                                          : 'text-gray-300'
                                      }`}
                                    />
                                  ))}
                                </div>
                                {review.verified && (
                                  <div className="bg-blue-500 rounded-full p-0.5">
                                    <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                              <img src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" alt="Google" className="h-3 opacity-60" />
                            </div>

                            {/* Review Text */}
                            <p className="text-gray-700 text-sm leading-relaxed mb-3 line-clamp-3 min-h-[60px]">
                              {review.text || "Excelente experiencia."}
                            </p>

                            {/* Reviewer Info */}
                            <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                              <div className="w-8 h-8 rounded-full overflow-hidden bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                                {review.author_photo ? (
                                  <img src={review.author_photo} alt={review.author_name} className="w-full h-full object-cover" />
                                ) : (
                                  <span>{review.author_name?.charAt(0) || 'U'}</span>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-semibold text-gray-900 text-xs truncate">{review.author_name}</p>
                                <p className="text-xs text-gray-500">{review.time}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Controls */}
            <div className="flex items-center justify-center mt-6 gap-4">
              <button
                onClick={() => setCurrentReviewIndex(prev => prev === 0 ? totalPages - 1 : prev - 1)}
                className="p-2 rounded-full bg-chocolate/10 hover:bg-chocolate/20 transition-colors"
                aria-label="Anterior"
              >
                <ChevronLeft className="w-5 h-5 text-chocolate" />
              </button>
              
              {/* Page Indicators */}
              <div className="flex gap-2">
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentReviewIndex(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      currentReviewIndex === index 
                        ? 'bg-chocolate w-6' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                    aria-label={`Página ${index + 1}`}
                  />
                ))}
              </div>
              
              <button
                onClick={() => setCurrentReviewIndex(prev => prev >= totalPages - 1 ? 0 : prev + 1)}
                className="p-2 rounded-full bg-chocolate/10 hover:bg-chocolate/20 transition-colors"
                aria-label="Siguiente"
              >
                <ChevronRight className="w-5 h-5 text-chocolate" />
              </button>
            </div>
            
            {/* Review counter */}
            <p className="text-center text-sm text-gray-500 mt-3">
              Mostrando {Math.min((currentReviewIndex + 1) * reviewsPerPage, googleReviews.reviews.length)} de {googleReviews.reviews.length} reseñas
            </p>
          </div>
        ) : (
          <p className="text-center text-gray-600">No hay reseñas disponibles en este momento.</p>
        )}

        {/* CTA to leave review */}
        <div className="text-center mt-10">
          <a
            href="https://g.page/r/CcimwRz2IYC5EBM/review"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#4285f4] hover:bg-[#3367d6] text-white px-6 py-3 rounded-full font-semibold shadow-lg transition-all duration-200 hover:shadow-xl"
          >
            <Star className="w-5 h-5 fill-white" />
            Deja tu reseña en Google
          </a>
        </div>
      </div>
    </section>
  );
};

export default ReviewsCarousel;
